import React, { useReducer, useRef, useCallback, useEffect, useState } from "react";
import axios from "axios";
import L from "leaflet";
import { TileLayer } from "react-leaflet";
import { normaliseTenementFeatureData } from "../utils/geoJsonHelpers.js";
import { createTenementInfoUrl } from "../utils/tenementsGeoServer.js";

const initialState = {
  markers: [],
  layers: [],
  selectedTenement: null,
  offlineCoastline: null,
  mapLoaded: false,
  mapDelay: true,
  filterActive: false,
  activeCompany: null,
  markerSetKey: "",
};

// Reducer for map state management
function mapStateReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER_ACTIVE":
      return { ...state, filterActive: true, activeCompany: action.company };
    case "CLEAR_FILTER":
      return { ...state, filterActive: false, activeCompany: null };
    case "SET_MARKERS":
      return { ...state, markers: action.markers, markerSetKey: action.markerSetKey ?? state.markerSetKey };
    case "SET_LAYERS":
      return { ...state, layers: action.layers };
    case "SET_SELECTED_TENEMENT":
      return { ...state, selectedTenement: action.tenement };
    case "SET_COASTLINE":
      return { ...state, offlineCoastline: action.coastline };
    case "SET_MAP_LOADED":
      return { ...state, mapLoaded: action.loaded };
    case "SET_MAP_DELAY":
      return { ...state, mapDelay: action.delay };
    default:
      return state;
  }
}

export function useMapState(projects = [], tenementsLayerVisible, clearNextBounds, mapLayers, sidebarItems) {
  const [state, dispatch] = useReducer(mapStateReducer, initialState);
  const [markersReady, setMarkersReady] = useState(false);
  const mapRef = useRef(null);

  // Create layer components from map layer data
  const createLayerComponents = useCallback(
    (layers = []) => {
      if (!layers || layers.length === 0) return [];

      return layers.map((layer) => {
        const shouldUseTms = layer.hasOwnProperty("tms") ? layer.tms : !(layer.url && layer.url.includes("openstreetmap.org"));
        const layerOpacity = layer.opacity !== undefined ? layer.opacity : 1;
        const sidebarIndex = sidebarItems ? sidebarItems.findIndex((item) => item.id === layer.id) : -1;
        const zIndex = sidebarIndex !== -1 ? 1000 - sidebarIndex : 100;

        return (
          <TileLayer
            key={layer.id}
            url={layer.url || "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
            attribution={layer.attribution || ""}
            opacity={layerOpacity}
            zIndex={zIndex}
            pane="tilePane"
            tms={shouldUseTms}
            noWrap={true}
            bounds={[
              [-85, -180],
              [85, 180],
            ]}
          />
        );
      });
    },
    [sidebarItems]
  );

  // Update layers when mapLayers changes
  useEffect(() => {
    if (mapLayers?.loaded && mapLayers.loaded.length > 0) {
      const layerComponents = createLayerComponents(mapLayers.loaded);
      dispatch({ type: "SET_LAYERS", layers: layerComponents });
    } else {
      dispatch({ type: "SET_LAYERS", layers: [] });
    }
  }, [mapLayers, createLayerComponents]);

  // Fetch offline coastline data on mount and handle initial map delay
  useEffect(() => {
    const fetchCoastlineData = async () => {
      try {
        const response = await axios.get("/static/data/50m_land.geojson");
        dispatch({ type: "SET_COASTLINE", coastline: response.data });
      } catch (error) {
        console.error("Error fetching coastline data:", error);
      }
    };

    fetchCoastlineData();

    // Set map delay timeout
    const timer = setTimeout(() => {
      dispatch({ type: "SET_MAP_DELAY", delay: false });
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  // Extract marker creation to its own function for reuse
  const createMarkersFromProjects = useCallback((projectsToRender) => {
    if (!projectsToRender || projectsToRender.length === 0) return [];

    return projectsToRender
      .map((project, index) => {
        if (!project || !project.latitude || !project.longitude) {
          return null;
        }
        return { project, index };
      })
      .filter(Boolean); // Filter out nulls
  }, []);

  // Movement handler to store map center and zoom
  const handleMovement = useCallback(() => {
    if (!mapRef.current) return;

    const center = mapRef.current.getCenter();
    localStorage.setItem("mapCenterLat", center.lat);
    localStorage.setItem("mapCenterLng", center.lng);
    localStorage.setItem("mapZoom", mapRef.current.getZoom());
  }, []);

  // Click handler for tenement info.
  const getTenementInfo = useCallback(
    (e) => {
      if (!tenementsLayerVisible || !e || !e.latlng) return;

      axios
        .get(createTenementInfoUrl(e.latlng))
        .then((response) => {
          dispatch({ type: "SET_SELECTED_TENEMENT", tenement: normaliseTenementFeatureData(response.data) });
        })
        .catch((error) => {
          console.error("Error fetching tenement info:", error);
          dispatch({ type: "SET_SELECTED_TENEMENT", tenement: null });
        });
    },
    [tenementsLayerVisible]
  );

  // Helper function to normalize projects array
  const normalizeProjects = useCallback((projects) => {
    if (!projects) return [];
    return Array.isArray(projects) ? projects : Object.keys(projects).map((key) => projects[key]);
  }, []);

  // Helper function to get valid projects
  const getValidProjects = useCallback(
    (projects) => {
      const projectArray = normalizeProjects(projects);
      return projectArray.filter(
        (project) =>
          project && project.latitude && project.longitude && !isNaN(Number(project.latitude)) && !isNaN(Number(project.longitude))
      );
    },
    [normalizeProjects]
  );

  const getMarkerSetKey = useCallback((projectsToRender) => {
    return projectsToRender
      .map((project) => `${project.project_id}:${project.latitude}:${project.longitude}`)
      .join("|");
  }, []);

  // Combined marker filtering and management
  const filterMarkersByCompany = useCallback(
    (companyIdentifier) => {
      if (!projects) {
        console.warn("filterMarkersByCompany called but projects is undefined");
        return;
      }

      const projectArray = normalizeProjects(projects);

      // Filter projects that belong to this company
      const filteredProjects = projectArray.filter((project) => {
        return project && (project.company_slug === companyIdentifier || project.company_id === companyIdentifier);
      });

      if (filteredProjects.length === 0) {
        console.warn(`No projects found for company: ${companyIdentifier}`);
        return;
      }

      // Set filter status to active
      dispatch({ type: "SET_FILTER_ACTIVE", company: companyIdentifier });

      // Get valid projects with lat/lng
      const validProjects = getValidProjects(filteredProjects);

      // Create markers
      const newMarkers = createMarkersFromProjects(validProjects);
      dispatch({ type: "SET_MARKERS", markers: newMarkers, markerSetKey: getMarkerSetKey(validProjects) });

      // Zoom to fit these markers
      if (mapRef.current && validProjects.length > 0) {
        const positions = validProjects.map((project) => L.latLng(Number(project.latitude), Number(project.longitude)));

        if (positions.length > 0) {
          const bounds = L.latLngBounds(positions);
          mapRef.current.flyToBounds(bounds, { padding: [50, 50] });
        }
      }
    },
    [projects, createMarkersFromProjects, normalizeProjects, getValidProjects, getMarkerSetKey]
  );

  const updateAllMarkers = useCallback(() => {
    if (!projects) {
      console.warn("updateAllMarkers called but projects is undefined");
      return;
    }

    if (!mapRef.current) {
      return;
    }

    if (!state.mapLoaded) {
      return;
    }

    const validProjects = getValidProjects(projects);

    const markerSetKey = getMarkerSetKey(validProjects);

    // Rebuild when the actual project set changes, even if the result count is unchanged.
    if (state.markerSetKey !== markerSetKey) {
      dispatch({ type: "CLEAR_FILTER" });
      const newMarkers = createMarkersFromProjects(validProjects);
      dispatch({ type: "SET_MARKERS", markers: newMarkers, markerSetKey });
    }
  }, [projects, createMarkersFromProjects, getValidProjects, getMarkerSetKey, state.markerSetKey, state.mapLoaded]);

  // Add a separate effect to create markers only after map is ready
  useEffect(() => {
    // Only update markers when map is loaded and we have projects
    if (state.mapLoaded && mapRef.current && projects && !markersReady) {
      if (Array.isArray(projects) ? projects.length > 0 : Object.keys(projects).length > 0) {
        // Added a small delay to ensure map is fully initialized
        const timer = setTimeout(() => {
          updateAllMarkers();
          setMarkersReady(true);
        }, 100);

        return () => clearTimeout(timer);
      }
    }
  }, [state.mapLoaded, projects, markersReady, updateAllMarkers, mapRef]);

  // Map initialization
  const onMapCreated = useCallback(
    (map) => {
      if (!map) return;

      // Store map reference
      mapRef.current = map;

      try {
        // Create panes
        map.createPane("basePane");
        map.createPane("tilePane");
        map.createPane("overlayPane");
        map.createPane("markerPane");

        // Set z-index for panes
        map.getPane("basePane").style.zIndex = "10";
        map.getPane("tilePane").style.zIndex = "200";
        map.getPane("overlayPane").style.zIndex = "300";
        map.getPane("markerPane").style.zIndex = "400";

        // Configure map options
        map._zoomAnimated = true;
        map.options.zoomAnimation = false;
        map.options.fadeAnimation = false;
        map._loaded = true;

        // Restore map position from localStorage
        const lat = parseFloat(localStorage.getItem("mapCenterLat"));
        const lng = parseFloat(localStorage.getItem("mapCenterLng"));
        const zoom = parseInt(localStorage.getItem("mapZoom"));

        if (!isNaN(lat) && !isNaN(lng) && !isNaN(zoom)) {
          map.setView({ lat, lng }, zoom, { animate: false });
        } else {
          // Default to Australia
          map.setView([-25.2744, 133.7751], 5, { animate: false });
        }

        map.on("moveend", handleMovement);
        map.on("zoomend", handleMovement);

        setTimeout(() => {
          dispatch({ type: "SET_MAP_LOADED", loaded: true });
        }, 100);
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    },
    [handleMovement]
  );

  return {
    state,
    dispatch,
    mapRef,
    handleMovement,
    getTenementInfo,
    filterMarkersByCompany,
    updateAllMarkers,
    onMapCreated,
    createMarkersFromProjects,
  };
}
